import { theme } from 'antd';
import { formatPhoneNumber } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectContactByPersonID } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { Input, Typography } from 'zui/Antd';

const { Text } = Typography;

const EditContactInput = ({ personID, error, setError }) => {
	// Theme tokens
	const {
		token: { fontSizeLG, controlHeight, colorError }
	} = theme.useToken();

	const dispatch = useDispatch();
	const canEdit = useManagePermittedRoles('personInformationCUD');
	const contact = useSelector((state) => selectContactByPersonID(state, personID));

	// Local state
	const [editableContact, setEditableContact] = useState('');
	const [lastValidContact, setLastValidContact] = useState('');

	useEffect(() => {
		const initial = contact?.contactInformation || '';
		setEditableContact(initial);
		setLastValidContact(initial);
	}, [contact, personID]);

	// Clean input: remove spaces, dashes, parentheses
	const normalizeInput = (value) => value.replace(/[\s()-]/g, '');

	// Validation rules
	const isValidLocal = (num) => /^0(2|3|4|7|8)\d{8}$/.test(num);
	const isValidIntl = (num) => /^\+61(2|3|4|7|8)\d{8}$/.test(num);

	const handleInputChange = (e) => {
		let value = e.target.value;

		// Keep only digits and one leading +
		value = value.replace(/[^\d+]/g, '');

		// Limit length: local 10 digits, intl 12 max
		if (value.startsWith('0')) value = value.slice(0, 10);
		else if (value.startsWith('+61')) value = value.slice(0, 12);

		setEditableContact(value);
		setError(false);
	};

	const handleValidateAndUpdate = (e) => {
		e.preventDefault();

		const cleaned = normalizeInput(editableContact);
		const isValid = isValidLocal(cleaned) || isValidIntl(cleaned);

		if (!cleaned || !isValid) {
			setEditableContact(lastValidContact); // rollback to last valid
			setError(true);
			return;
		}

		// Convert to international format if local
		const final = cleaned.startsWith('+') ? cleaned : `+61${cleaned.slice(1)}`;

		if (!_.isEqual(final, contact?.contactInformation)) {
			if (contact?.contactID) {
				dispatch(
					peopleActions.updatePersonContact({
						personID,
						contactID: contact.contactID,
						contactInformation: final
					})
				);
			} else {
				dispatch(
					peopleActions.addPersonContact({
						personID,
						contactInformation: final
					})
				);
			}
			setLastValidContact(final);
			setEditableContact(final);
			setError(false);
		}
	};

	if (!canEdit) {
		return (
			<Text style={{ fontSize: fontSizeLG }}>{formatPhoneNumber(contact?.contactInformation ?? '') || '-'}</Text>
		);
	}

	return (
		<Input
			style={{
				width: 150,
				textAlign: editableContact ? 'center' : 'left',
				height: controlHeight,
				fontSize: fontSizeLG,
				borderColor: error ? colorError : undefined
			}}
			status={error ? colorError : ''}
			value={formatPhoneNumber(editableContact)}
			onChange={handleInputChange}
			onPressEnter={handleValidateAndUpdate}
			onBlur={handleValidateAndUpdate}
			placeholder='Phone Number'
			type='tel'
		/>
	);
};

export default EditContactInput;
